import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@welldev-group/rcl-button';
import Link from '@welldev-group/rcl-link';
import { StringParam, useQueryParam } from 'use-query-params';
import withQueryParamsProvider from '../../../../shared/components/withQueryParamsProvider';
import httpClient from '../../../../shared/libraries/httpClient';
import routes from '../../../../shared/routes';
import DateTime from '../../DateTime';
import SubscriptionPageLayout from './SubscriptionPageLayout';
import NewPlanDetails from './NewPlanDetails';
import SubscriptionDivider from './SubscriptionDivider';
import ActiveUsersDetails from './ActiveUsersDetails';
import WorkspaceManagementSection from './WorkspaceManagementSection';
import ChannelManagementSection from './ChannelManagementSection';
import NoteList from './NoteList';
import SubscriptionWaitingModal from './SubscriptionWaitingModal';
import ActionConfirmationModal from './ActionConfirmationModal';
import SubscriptionErrorModal from './SubscriptionErrorModal';

const API_ERROR_FOR_SCHEDULED_DOWNGRADE = 'has_scheduled_downgrade';
const API_ERROR_FOR_IMPOSSIBLE_DOWNGRADE = 'not_possible';
const API_ERROR_FOR_INCORRECT_DOWNGRADE = 'would_be_upgrade';

const SubscriptionDowngrade = ({ affiliateId, urls, plan }) => {
  const [identifier] = useQueryParam('identifier', StringParam);
  const [period] = useQueryParam('period', StringParam);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [activeWorkspaces, setActiveWorkspaces] = useState([]);
  const [workspacesByID, setWorkspacesByID] = useState({});
  const [deactivateWorkspacesIDs, setDeactivateWorkspacesIDs] = useState([]);
  const [deactivateChannelsIDs, setDeactivateChannelsIDs] = useState([]);
  const [showWaitingModal, setShowWaitingModal] = useState(false);
  const [showFeatureDetailsModal, setShowFeatureDetailsModal] = useState(false);
  const [changePlanSuccessMessage, setChangePlanSuccessMessage] = useState('');
  const [errorText, setErrorText] = useState(null);
  const [errorRedirectUrl, setErrorRedirectUrl] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const noteItems = [
    {
      id: 0,
      note: I18n.t('components.subscription.downgrade_payment_text', {
        date: DateTime({
          dateTime: subscriptionDetails?.downgradeAt,
          format: 'short',
          returnStringOnly: true,
        }),
      }),
    },
    {
      id: 1,
      note: I18n.t('components.subscription.ndc_channels_availability_text'),
    },
    (subscriptionDetails?.newPlan.featureChanges.lessChannels === null ||
      subscriptionDetails?.newPlan.featureChanges?.lessChannels > 0) && {
      id: 2,
      note:
        subscriptionDetails?.newPlan.featureChanges.lessChannels === null
          ? I18n.t(
              'components.subscription.non_ndc_channels_downgrade_from_unlimited_text',
              {
                newChannelCount: subscriptionDetails?.newPlan.channels,
              },
            )
          : I18n.t(
              'components.subscription.non_ndc_channels_availability_text',
              {
                reductionCount:
                  subscriptionDetails?.newPlan.featureChanges.lessChannels,
              },
            ),
    },
    (subscriptionDetails?.newPlan.featureChanges.lessWorkspaces === null ||
      subscriptionDetails?.newPlan.featureChanges?.lessWorkspaces > 0) && {
      id: 3,
      note:
        subscriptionDetails?.newPlan.featureChanges.lessWorkspaces === null
          ? I18n.t(
              'components.subscription.workspaces_downgrade_from_unlimited_text',
              {
                newWorkspaceCount: subscriptionDetails?.newPlan.workspaces,
              },
            )
          : I18n.t('components.subscription.workspaces_availability_text', {
              reductionCount:
                subscriptionDetails?.newPlan.featureChanges.lessWorkspaces,
            }),
    },
    subscriptionDetails?.newPlan.featureChanges
      .customDomainNotSupportedAnymore && {
      id: 4,
      note: I18n.t('components.subscription.domain_not_available'),
    },
    subscriptionDetails?.newPlan.featureChanges.lessPhoneSupport !== null && {
      id: 5,
      note: I18n.t('components.subscription.support_by_phone_text', {
        count: parseFloat(subscriptionDetails?.newPlan.phone, 10),
      }),
    },
  ];

  const fetchSubscriptionDetails = async params => {
    try {
      const { data } = await httpClient.get(
        routes.billing.subscriptions.downgrade.fetch(params),
      );

      setSubscriptionDetails(data);
    } catch ({ response }) {
      if (
        response.status === 422 &&
        response.data.error === API_ERROR_FOR_SCHEDULED_DOWNGRADE
      ) {
        setErrorRedirectUrl(urls.plans);
        setErrorText(
          I18n.t('components.pricing.plans.downgrade_not_possible_text', {
            planName: I18n.t(plan.name, {
              scope: 'components.plans',
            }),
            planPeriod: plan.period,
          }),
        );
      } else if (
        response.status === 422 &&
        (response.data.error === API_ERROR_FOR_IMPOSSIBLE_DOWNGRADE ||
          response.data.error === API_ERROR_FOR_INCORRECT_DOWNGRADE)
      ) {
        setErrorRedirectUrl(urls.plans);
        setErrorText(
          I18n.t('components.pricing.plans.downgrade_failed_text', {
            planName: I18n.t(plan.name, {
              scope: 'components.plans',
            }),
            planPeriod: plan.period,
          }),
        );
      } else {
        setErrorRedirectUrl(null);
        setErrorText(response.data.error);
      }

      setShowErrorModal(true);
    }
  };

  const changePlan = async () => {
    setShowWaitingModal(true);

    const deactivateWorkspaces = [
      ...subscriptionDetails?.workspaces.collection
        .map(workspace => !workspace.active && workspace.id)
        .filter(workspaceId => Boolean(workspaceId)),
    ];
    const deactivateChannels = [
      ...subscriptionDetails?.channels.collection
        .map(channel => {
          const channelInfo = {
            supplier: channel?.supplier,
            account_identifier: channel?.accountIdentifier,
          };

          return !channel.active && channelInfo;
        })
        .filter(channel => Boolean(channel)),
    ];
    const payload = {
      plan_id: subscriptionDetails?.newPlan.id,
      deactivate_workspaces: deactivateWorkspaces,
      deactivate_channels: deactivateChannels,
    };

    httpClient
      .post(
        routes.billing.subscriptions.downgrade.changePlan({
          affiliate_id: affiliateId,
        }),
        payload,
      )
      .then(response => {
        if (response.status === 200) {
          setChangePlanSuccessMessage(response.data.message);
          setShowWaitingModal(false);
          setShowFeatureDetailsModal(true);
        }
      })
      .catch(({ response }) => {
        if (
          response.status === 422 &&
          response.data.error === API_ERROR_FOR_SCHEDULED_DOWNGRADE
        ) {
          setErrorRedirectUrl(urls.plans);
          setErrorText(
            I18n.t('components.pricing.plans.downgrade_not_possible_text', {
              planName: I18n.t(plan.name, {
                scope: 'components.plans',
              }),
              planPeriod: plan.period,
            }),
          );
        } else if (
          response.status === 422 &&
          (response.data.error === API_ERROR_FOR_IMPOSSIBLE_DOWNGRADE ||
            response.data.error === API_ERROR_FOR_INCORRECT_DOWNGRADE)
        ) {
          setErrorRedirectUrl(urls.plans);
          setErrorText(
            I18n.t('components.pricing.plans.downgrade_failed_text', {
              planName: I18n.t(plan.name, {
                scope: 'components.plans',
              }),
              planPeriod: plan.period,
            }),
          );
        } else {
          setErrorText(response.data.error);
        }

        setShowErrorModal(true);
        setShowWaitingModal(false);
      });
  };

  useEffect(() => {
    fetchSubscriptionDetails({
      affiliate_id: affiliateId,
      identifier,
      period,
      'deactivate_workspaces[]': deactivateWorkspacesIDs,
      'deactivate_channels[]': deactivateChannelsIDs,
    });
  }, [
    affiliateId,
    identifier,
    period,
    deactivateWorkspacesIDs,
    deactivateChannelsIDs,
  ]);

  useEffect(() => {
    if (subscriptionDetails?.workspaces.collection.length > 0) {
      const workspacesMap = {};
      const enabledWorkspaces = [];

      subscriptionDetails.workspaces.collection.forEach(workspace => {
        workspacesMap[workspace.id] = workspace.name;

        if (workspace.active) {
          enabledWorkspaces.push(workspace);
        }
      });

      setWorkspacesByID(workspacesMap);
      setActiveWorkspaces(enabledWorkspaces);
    }
  }, [subscriptionDetails]);

  return (
    <>
      <SubscriptionWaitingModal
        isVisible={showWaitingModal}
        waitingText={I18n.t('components.subscription.downgrade_waiting_text')}
      />
      <SubscriptionErrorModal
        isVisible={showErrorModal}
        errorText={errorText}
        errorRedirectUrl={errorRedirectUrl}
        onClose={() => setShowErrorModal(false)}
      />
      <ActionConfirmationModal
        isVisible={showFeatureDetailsModal}
        title={I18n.t(
          'components.subscription.subscription_feature_details_modal.downgrade.greeting_text',
        )}
        subTitle={changePlanSuccessMessage}
        footer={
          <Link
            type="button"
            size="small"
            modifier="primary"
            href={urls.billing}
          >
            {I18n.t(
              'components.subscription.subscription_feature_details_modal.downgrade.footer.link.close',
            )}
          </Link>
        }
      />
      <SubscriptionPageLayout
        title={I18n.t('components.subscription.downgrade_page_title')}
        footer={
          <>
            <Button
              label={I18n.t(
                'components.subscription.details_page.footer.downgrade',
              )}
              type="primary"
              size="small"
              disabled={
                subscriptionDetails?.workspaces.outOfLimit ||
                subscriptionDetails?.channels.outOfLimit
              }
              onClick={changePlan}
            />
            <Link
              type="button"
              size="small"
              modifier="default"
              href={urls.plans}
            >
              {I18n.t('components.subscription.details_page.footer.cancel')}
            </Link>
          </>
        }
      >
        <NewPlanDetails
          className="subscription-page__invoice-plan subscription-page__invoice-plan--tertiary-light"
          plan={subscriptionDetails?.newPlan}
          period={period}
          hidePeriodToggler={true}
        />
        <SubscriptionDivider className="subscription-page__invoice-divider" />
        <ActiveUsersDetails
          className="subscription-page__invoice-active-users"
          activeUsers={subscriptionDetails?.activeUsers}
          nextBillingDate={subscriptionDetails?.downgradeAt}
        />
        <WorkspaceManagementSection
          className="subscription-page__invoice-workplace-management-section"
          workspaces={subscriptionDetails?.workspaces.collection}
          outOfLimit={subscriptionDetails?.workspaces.outOfLimit}
          limit={subscriptionDetails?.newPlan.workspaces}
          activeWorkspacesCount={activeWorkspaces.length}
          setDeactivateWorkspacesIDs={setDeactivateWorkspacesIDs}
        />
        <ChannelManagementSection
          className="subscription-page__invoice-channel-management-section"
          channels={subscriptionDetails?.channels.collection}
          outOfLimit={subscriptionDetails?.channels.outOfLimit}
          limit={subscriptionDetails?.newPlan.channels}
          workspacesByID={workspacesByID}
          setDeactivateChannelsIDs={setDeactivateChannelsIDs}
        />
        <NoteList
          className="subscription-page__invoice-note"
          items={noteItems}
          termsOfUseUrl={urls.termsOfUse}
        />
      </SubscriptionPageLayout>
    </>
  );
};

SubscriptionDowngrade.propTypes = {
  affiliateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  plan: PropTypes.shape({
    name: PropTypes.string.isRequired,
    period: PropTypes.string.isRequired,
  }).isRequired,
  urls: PropTypes.shape({
    termsOfUse: PropTypes.string.isRequired,
    billing: PropTypes.string.isRequired,
    plans: PropTypes.string.isRequired,
  }).isRequired,
};

export default withQueryParamsProvider(SubscriptionDowngrade);
